import React, { useEffect, useState } from 'react';
import { KeyAnswer as UIKeyAnswer } from '@shopee/chatbot-ui';
import { InitStore } from '@/stores/init';
import { AgentStore } from '@/stores/agent';
import { FAQ, KeyAnswerStatus } from '@shopee/chatbot-ui/lib/components/KeyAnswer';
import { getTrans, handleClickHistoryButton } from '@/utils/tools';
import { inject, observer } from 'mobx-react';
import { getChildInject } from '@/utils/instantLoaded/tools';
import sendBotMessageToAgent from '@/utils/sendBotMessageToAgent';
import handleClickIntent from '@/utils/intent/handleClickIntent';
import API from '@/services';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/tracking/constants';
import { IntentSource, IntentType } from '@/types/intent';
import { HISTORY_TRANSIFY_KEY, TRACKING_MSG_TYPE } from '@/utils/constants';
import { updateHistoryDialogueData, updateHistoryDialogueOuterData } from '@/utils/historyUtils';
import { ChatbotUIBasicProps } from '@/types/common';
import { trackingHandler } from '@/utils/btnClickHandler';
import { isFullSummary, isAutoAdapt, isScrollToUnread } from '@/utils/experiment/getKeyAnswerUIUXExperiment';
import { featureToggle, FEATURE_TOGGLE_TYPE } from '@/utils/featureToggleConfig';
import { isBank } from '@/config/core';

type KeyAnswerProps = {
  agentStore: AgentStore;
  initStore: InitStore;

  nodePointId: string;
  dialogueId: string;
  messageId: string;

  historyDialogueId?: string;
  historySessionId?: string;
  isHistory: boolean;
  listPos: number;

  thread_id?: string;
  thread_node_id?: string;
  thread_id_v2?: string;
  round_id?: string;

  faqTitle: string;
  faqIntentList: FAQ[];
  summary: string;
  supportingMessage: string;

  kbIntentId?: string;
  kbAnswerId?: string;
  kbEdition?: string;
  kbBotId?: string;
  kbBotName?: string;

  // 为了faq上报加source
  source?: number;

  customCallback?: ChatbotUIBasicProps['customCallback'];
  useUIOptimisationRecommender?: boolean;
};

const KeyAnswer: React.FC<KeyAnswerProps> = (props) => {
  const {
    faqTitle,
    faqIntentList,
    summary,
    supportingMessage,
    isHistory,
    customCallback = {},
    useUIOptimisationRecommender,
  } = props;
  const [status, setStatus] = useState<KeyAnswerStatus>(KeyAnswerStatus.FINISH);

  const onClickFaq = (faq: FAQ, option?: { trackingData?: any; isInDetail?: boolean }) => {
    const { agentStore } = props;
    const { trackingData, isInDetail } = option || {};

    // 在agent过程中，走agent逻辑
    if (agentStore.inAgent) {
      sendBotMessageToAgent({ content: faq.text });
    } else {
      // 走ChatBot逻辑
      const params = { ...(faq?.query_callback || {}) };
      const newParams: any = { ...params, ...customCallback };
      handleClickIntent(faq.text, newParams, (res: any) => {
        // 因为需要在点击时添加 next_chat_dialogue_id 这个 id 需要请求后才获得，所以把相应参数从 chat ui 传回这里，然后触发点击上报
        if (trackingData) {
          trackingHandler({
            trackingname: isInDetail ? TRACKING_TYPE.KEY_ANSWER_FAQ_IN_DETAIL : TRACKING_TYPE.KEY_ANSWER_FAQ,
            trackingdata: { ...trackingData, next_chat_dialogue_id: res?.dialogue_id },
          });
        }
      });
    }
  };

  const onClickDisabledFaq = () => {
    handleClickHistoryButton();
  };

  const loadKeyAnswer = async () => {
    const params = {
      current_session_id: props.initStore?.sessionId,
      kb_edition: props.kbEdition,
      kb_bot_id: props.kbBotId,
      kb_intent_id: props.kbIntentId,
      kb_answer_id: props.kbAnswerId,
    };

    updateHistoryDialogueOuterData(
      {
        showMaxWidth: true,
        showReloadIcon: false,
      },
      props.messageId,
    );
    setStatus(KeyAnswerStatus.LOADING);

    const data = await (API as { getArticleContent: Function }).getArticleContent(params).catch(() => {});

    updateHistoryDialogueOuterData(
      {
        showMaxWidth: false,
        showReloadIcon: false,
      },
      props.messageId,
    );

    if (!data || !data?.summary) {
      updateHistoryDialogueOuterData(
        {
          showReloadIcon: true,
          onReloadClick: loadKeyAnswer,
        },
        props.messageId,
      );
      setStatus(KeyAnswerStatus.ERROR);
    } else {
      updateHistoryDialogueData(
        {
          summary: data?.summary || '',
          supportingMessage: data?.supporting_message || '',
        },
        props.messageId,
      );
      setStatus(KeyAnswerStatus.FINISH);
    }
  };

  // 历史消息中默认返回summary数据，如果没有再发一次单独的article接口去请求
  useEffect(() => {
    if (isHistory && !summary) {
      setStatus(KeyAnswerStatus.LOADING);
    }
  }, [isHistory, summary]);

  const trackingData = {
    chat_session_id: props.isHistory ? props.historySessionId : props.initStore?.sessionId,
    chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
    bot_id: props.kbBotId,
    bot_name: props.kbBotName,
    if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
  };
  const trackingProps = {
    componentTrackingData: {
      keyAnswer: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.KEY_ANSWER],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.KEY_ANSWER],
        trackingdataBasic: {
          ...trackingData,
          button_text: getTrans('key_answer_view_more_btn'),
          intent_id: props.kbIntentId,
        },
      },
      faq: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.KEY_ANSWER_FAQ],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.KEY_ANSWER_FAQ],
        trackingdataBasic: trackingData,
        trackingItemData: (data: FAQ & { location: number }) => ({
          location: data?.location,
          content: data?.hotquestion_text,
          intent_source: data?.intent_source,
          intent_id: data?.intent_id,
          is_ds_predicted: data?.intent_type === IntentType.Prediction,
        }),
      },
      faqInDetail: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.KEY_ANSWER_FAQ_IN_DETAIL],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.KEY_ANSWER_FAQ_IN_DETAIL],
        trackingdataBasic: trackingData,
        trackingItemData: (data: FAQ & { location: number; query_callback: { initial_intent: string } }) => ({
          location: data?.location,
          content: data?.hotquestion_text,
          intent_source: data?.intent_source,
          intent_id: data?.intent_id,
          is_ds_predicted: data?.intent_type === IntentType.Prediction,
          initial_intent: data?.query_callback?.initial_intent,
          is_faq: props.source === 4 ? true : false,
          kb_intent_id: data?.intent_source === IntentSource.KnowledgeBase ? data?.intent_id : undefined,
        }),
      },
      closePopup: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.KEY_ANSWER_CLOSE_POPUP],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.KEY_ANSWER_CLOSE_POPUP],
        trackingdataBasic: {
          ...trackingData,
          intent_id: props.kbIntentId,
        },
      },
    },
  };

  return (
    <UIKeyAnswer
      status={status}
      viewMoreText={getTrans('key_answer_view_more_btn')}
      unreadText={getTrans('unread_text')}
      disabledFaq={isHistory}
      faqTitle={faqTitle}
      summaryMaxHeight={isFullSummary() ? 400 : 240}
      isAutoAdapt={isAutoAdapt()}
      isScrollToUnread={isScrollToUnread()}
      faqIntentList={faqIntentList}
      summary={summary}
      supportingMessage={supportingMessage}
      onClickFaq={onClickFaq}
      loadKeyAnswer={loadKeyAnswer}
      onClickDisabledFaq={onClickDisabledFaq}
      failToLoadText={getTrans(HISTORY_TRANSIFY_KEY.FAIL_TO_LOAD_FILE)}
      useUIOptimisationRecommender={useUIOptimisationRecommender}
      isUIOptimisationAccordion={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_ACCORDION)}
      isUIOptimisationButton={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_BUTTON)}
      isUIOptimisationTable={featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.UI_OPTIMISATION_PART_3_TABLE)}
      popupClassName={isBank() ? 'chatbot-bank-key-answer-modal' : ''}
      {...(trackingProps as any)}
    />
  );
};

export default inject(getChildInject('initStore', 'agentStore'))(observer(KeyAnswer));
